'use client'

import React, { useMemo } from 'react'
import { Box, Drawer, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'

import type { ClientSessionMetadata } from '@/core/models/medconnect-agent.model'
import { ChatHistoryContent } from './ChatHistoryContent'

const getSessionReferenceDate = (session: ClientSessionMetadata): Date => {
  const createdTime = new Date(session.createdAt || 0)
  const updatedTime = new Date(session.updatedAt || 0)
  return updatedTime > createdTime ? updatedTime : createdTime
}

// Helper to group sessions by date
const groupSessions = (sessionList: ClientSessionMetadata[] = [], t: (key: string) => string) => {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)
  const sevenDaysAgo = new Date(today)
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)
  const thirtyDaysAgo = new Date(today)
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
  const startOfYear = new Date(now.getFullYear(), 0, 1)

  const groups: Record<string, ClientSessionMetadata[]> = {
    [t('page.medconnectAgent.chatHistory.groups.today')]: [],
    [t('page.medconnectAgent.chatHistory.groups.yesterday')]: [],
    [t('page.medconnectAgent.chatHistory.groups.last7Days')]: [],
    [t('page.medconnectAgent.chatHistory.groups.last30Days')]: [],
    [t('page.medconnectAgent.chatHistory.groups.earlierThisYear')]: [],
    [t('page.medconnectAgent.chatHistory.groups.older')]: [],
  }

  // Sort sessions by reference date (max of createdAt/updatedAt) descending before grouping
  const sortedSessions = [...sessionList].sort((a, b) => getSessionReferenceDate(b).getTime() - getSessionReferenceDate(a).getTime())

  sortedSessions.forEach((session) => {
    const sessionDate = getSessionReferenceDate(session)
    if (sessionDate >= today) {
      groups[t('page.medconnectAgent.chatHistory.groups.today')].push(session)
    } else if (sessionDate >= yesterday) {
      groups[t('page.medconnectAgent.chatHistory.groups.yesterday')].push(session)
    } else if (sessionDate >= sevenDaysAgo) {
      groups[t('page.medconnectAgent.chatHistory.groups.last7Days')].push(session)
    } else if (sessionDate >= thirtyDaysAgo) {
      groups[t('page.medconnectAgent.chatHistory.groups.last30Days')].push(session)
    } else if (sessionDate >= startOfYear) {
      groups[t('page.medconnectAgent.chatHistory.groups.earlierThisYear')].push(session)
    } else {
      groups[t('page.medconnectAgent.chatHistory.groups.older')].push(session)
    }
  })
  return groups
}

interface ChatHistoryDrawerProps {
  sessions: ClientSessionMetadata[] | undefined
  isLoadingSessions: boolean
  sessionsError: any
  selectedSessionId: string | null | undefined
  onSessionSelect: (sessionId: string) => void
  onNewChat: () => void
  variant?: 'drawer' | 'sidebar'
  drawerOpen?: boolean
  onDrawerClose?: () => void
  drawerWidth?: number
  isCollapsed?: boolean
  onCollapseToggle?: () => void
}

export const ChatHistoryDrawer: React.FC<ChatHistoryDrawerProps> = ({
  sessions,
  isLoadingSessions,
  sessionsError,
  selectedSessionId,
  onSessionSelect,
  onNewChat,
  variant,
  drawerWidth = 280,
  drawerOpen,
  onDrawerClose,
  isCollapsed = false,
  onCollapseToggle,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()

  // Auto-detect variant if not explicitly provided
  const detectedVariant: 'drawer' | 'sidebar' = variant || (drawerOpen !== undefined && onDrawerClose !== undefined ? 'drawer' : 'sidebar')

  // Determine if mobile based on theme breakpoints
  const isMobile = detectedVariant === 'drawer'

  const groupedSessions = useMemo(() => {
    return sessions ? groupSessions(sessions, t) : {}
  }, [sessions, t])

  const drawerContent = (
    <ChatHistoryContent
      groupedSessions={groupedSessions}
      isLoadingSessions={isLoadingSessions}
      sessionsError={sessionsError}
      sessions={sessions}
      selectedSessionId={selectedSessionId}
      onSessionSelect={onSessionSelect}
      onNewChat={onNewChat}
      isCollapsed={isCollapsed}
      isMobile={isMobile}
      onCollapseToggle={onCollapseToggle}
      detectedVariant={detectedVariant}
    />
  )

  if (detectedVariant === 'sidebar') {
    return <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>{drawerContent}</Box>
  }

  // Render as Drawer
  return (
    <Box component="nav">
      <Drawer
        variant="temporary"
        anchor="right"
        open={drawerOpen}
        onClose={onDrawerClose}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: 'block', // Show on all devices
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            pt: { xs: theme.spacing(7), sm: theme.spacing(8) },
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </Box>
  )
}

export default ChatHistoryDrawer
